import { useBroadcastChannel } from '@vueuse/core'
import { watch } from 'vue'

export enum AuthChannelMessage {
  ChangeCompany = 'changeCompany', // triggered when the user changes company or resets to main company
}

export enum CompanyPlanChannelMessage {
  FetchPlan = 'fetchPlan', // triggered when the fetchCompanyPlan is called this is also called when credits or activeJobs are changed
}

/**
 * Store to handle broadcast channel communication.
 * We need to have this in a store so that it is a singleton and can be used across the app. Since vueuse does close the channel when the component is unmounted.
 * We use this to notify other tabs when the user changes company so they can reload.
 * NOTE! This only works with composition stores since we need to use the setup syntax to use vueuse.
 */
export const useBroadcastStore = defineStore('broadcast', () => {
  const authChannel = useBroadcastChannel<AuthChannelMessage, AuthChannelMessage>({
    name: 'auth-channel',
  })

  const companyPlanChannel =  useBroadcastChannel<CompanyPlanChannelMessage, CompanyPlanChannelMessage>({
    name: 'company-plan-channel',
  })

  // Watch incoming messages from the company plan channel
  watch(companyPlanChannel.data, async (msg) => {
    if (msg === CompanyPlanChannelMessage.FetchPlan) {
      const companyStore = useCompanyStore()

      await companyStore.fetchCompanyPlan(true)
    }
  }, { immediate: true })

  // Watch incoming messages from the broadcast channel
  watch(authChannel.data, (msg) => {
    if (msg === AuthChannelMessage.ChangeCompany) {
      window.location.reload()
    }
  }, { immediate: true })

  function postAuthChannelMessage(message: AuthChannelMessage) {
    authChannel.post(message)
  }

  function postCompanyPlanChannelMessage(message: CompanyPlanChannelMessage) {
    companyPlanChannel.post(message)
  }

  return {
    authChannel,
    companyPlanChannel,
    postCompanyPlanChannelMessage,
    postAuthChannelMessage,
  }
})
